/**
 * P9-P1 — Language selection + device detection + preference persistence.
 *
 * Resolves the active UI language using a precedence chain, CLAMPED to the set
 * of languages the platform has actually activated (`active_languages` from
 * public settings, default `['en']`). Because Arabic is not active in P9-P1,
 * every resolution returns `en` → the app stays English-only and unchanged.
 *
 * Precedence (highest first):
 *   1. Explicit stored choice (`localStorage[snappipit_language]`)
 *   2. Authenticated user's saved preference (`user.language`)
 *   3. Device / browser language (`navigator.languages`)
 *   4. Platform default (`settings.localization.default_language`)
 *   5. `en`
 * The chosen candidate is then clamped to `active_languages`.
 */
import i18n, { SUPPORTED_LANGUAGES } from './index';

const STORAGE_KEY = 'snappipit_language';
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

// Module state, hydrated from public settings when they load.
let activeLanguages = ['en'];
let settingsDefault = 'en';
// Remember the authenticated user's server-side preference while public settings
// are still loading. This lets us re-resolve the desired language after the
// active-language rollout gate has hydrated instead of getting stuck on English.
let preferredUserLanguage = null;

function base(lng) {
  return String(lng || '').split('-')[0].split('_')[0].toLowerCase();
}

export function isRtlLanguage(lng) {
  return RTL_LANGUAGES.has(base(lng));
}

export function getActiveLanguages() {
  return activeLanguages;
}

function clamp(lng) {
  const b = base(lng);
  if (activeLanguages.includes(b)) return b;
  if (activeLanguages.includes(settingsDefault)) return settingsDefault;
  return activeLanguages[0] || 'en';
}

export function getStoredLanguage() {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

export function detectDeviceLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || 'en'];
  for (const n of list) {
    const b = base(n);
    if (b) return b;
  }
  return 'en';
}

export function resolveLanguage({ userLanguage } = {}) {
  const candidate =
    getStoredLanguage() ||
    userLanguage ||
    detectDeviceLanguage() ||
    settingsDefault ||
    'en';
  return clamp(candidate);
}

function reconcile() {
  // P9-P2B: a dev/QA language probe (?lang=ar) must outrank the active-languages
  // clamp on non-admin surfaces, otherwise a late-loading /settings/public would
  // revert the preview. Admin routes are pinned to English in direction.jsx, so
  // honouring the probe here is harmless for admin.
  if (readLanguageProbe()) return;
  const next = resolveLanguage({ userLanguage: preferredUserLanguage });
  if (i18n.language !== next) i18n.changeLanguage(next);
}

/** Hydrate the active-language set from public settings (called once on load). */
export function setActiveLanguages(langs) {
  if (Array.isArray(langs) && langs.length) {
    const next = Array.from(new Set(langs.map(base).filter((lng) => SUPPORTED_LANGUAGES.includes(lng))));
    activeLanguages = next.length ? next : ['en'];
    reconcile();
  }
}

export function setSettingsDefaultLanguage(lng) {
  if (lng) settingsDefault = base(lng);
}

/** Apply + persist an explicit user choice (clamped). Used by a future switcher. */
export function applyLanguage(lng, { persist = true } = {}) {
  const next = clamp(lng);
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  }
  if (i18n.language !== next) i18n.changeLanguage(next);
  return next;
}

/** Apply the resolved language for the (optionally) authenticated user. */
export function applyUserLanguagePreference(userLanguage) {
  preferredUserLanguage = userLanguage ? base(userLanguage) : null;
  const next = resolveLanguage({ userLanguage: preferredUserLanguage });
  if (i18n.language !== next) i18n.changeLanguage(next);
  return next;
}

const SUPPORTED = new Set(SUPPORTED_LANGUAGES);

/**
 * Dev/QA language probe (opt-in, never auto). `?lang=<code>` previews any supported language even
 * while it is gated OFF for real users (bypasses the active-languages clamp);
 * `?lang=en` / `?lang=0` clears it. Persisted per-browser so it survives
 * navigation, exactly like the `?rtl=1` probe. Returns the probed base or null.
 */
export function readLanguageProbe() {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q != null) {
      const b = base(q);
      if (b && b !== '0' && SUPPORTED.has(b)) {
        window.localStorage.setItem('snappipit_lang_probe', b);
        return b;
      }
      window.localStorage.removeItem('snappipit_lang_probe');
      return null;
    }
    const stored = window.localStorage.getItem('snappipit_lang_probe');
    return stored && SUPPORTED.has(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Apply a language IGNORING the active-languages clamp (probe/admin preview). */
export function applyLanguageUnclamped(lng) {
  const b = base(lng);
  if (!SUPPORTED.has(b)) return i18n.language;
  if (i18n.language !== b) i18n.changeLanguage(b);
  return b;
}
